/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 04.06.17.
 */

import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import _ from "underscore";

import { asNumber } from "react-jsonschema-form/lib/utils";
import t from "../../../../trans";
import TempResourceContainer from "../../TempResourceContainer";
import SchemaRegistry from "../../../../services/SchemaRegistry";

/**
 * Works like SelectWidget but SELECT box value list will be propagated from the specified REST resource collection.
 *
 * In the UI Schema you should specify:
 *  {
 *      "ui:widget": "ObjectSelectWidget",
 *      "ui:options": {
 *          "resource": "person",
 *          "valueProperty": "id"
 *          "labelProperty": "name",
 *          "filter": {fieldName: fieldValue, ...},
 *          "sort": {fieldName: fieldValue},
 *          "enumOptions": [{"name": label, "id": value}]
 *      }
 *  }
 *
 *  Where:
 *      * "resource" - resource name from where to select list of values
 *      * "valueProperty" - property used as SELECT box value. In case the field coresponds to the object this value
 *          will be used to find the correct object (that is - this field walue should be unique).
 *          If this option is not specified then "idProperty" value from the resource's JSON Schema will be used.
 *      * "labelProperty" - Values of this resource property will be displayed in SELECT box list field and also used
 *        in the search. If this field is not specified then "name" will be used.
 *      * "filter" - additional filter for applying to resources list. Values can contain references to properties.
 *      * "sort" - sort for applying to resources list. By default resources are sorted based by "labelProperty" attribute in ascending order
 *      * "enumOptions" - this property values will be used as initial values for SELECT box until the response from the first REST query will be received
 *      * "searchable" - whether to enable search functionality (boolean)
 *
 *  Widget creates value that corresponds to the JSON Schema for this field.
 *
 *  Options are loaded once on mount (and when the resource changes) via getCollection. Typing is filtered client-side.
 */
class ObjectSelectWidget extends TempResourceContainer {
    static propTypes = {
        schema: PropTypes.object.isRequired,
        id: PropTypes.string.isRequired,
        options: PropTypes.shape({
            resource: PropTypes.string.isRequired,
            valueProperty: PropTypes.string,
            labelProperty: PropTypes.string,
            filter: PropTypes.object,
            enumOptions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            })),PropTypes.bool]),
            searchable: PropTypes.bool
        }).isRequired,
        name: PropTypes.string,
        value: PropTypes.any,
        required: PropTypes.bool,
        disabled: PropTypes.bool,
        readonly: PropTypes.bool,
        multiple: PropTypes.bool,
        autofocus: PropTypes.bool,
        registry: PropTypes.shape({
            formContext: PropTypes.shape({
                locale: PropTypes.string,
                params: PropTypes.shape({
                    path: PropTypes.string.isRequired,
                    resource: PropTypes.string.isRequired,
                    action: PropTypes.oneOf(["list","new","view","edit","delete"]).isRequired,
                    id: PropTypes.string,
                    query: PropTypes.object,
                    parentPath: PropTypes.string,
                    parentResource: PropTypes.string,
                    parentAction: PropTypes.oneOf(["list","new","view","edit","delete"]),
                    parentId: PropTypes.string,
                    parentField: PropTypes.string
                })
            }).isRequired,
        }),
        onChange: PropTypes.func.isRequired,
        onBlur: PropTypes.func,
        placeholder: PropTypes.string,
        className: PropTypes.string
    };

    static defaultProps = {
        autofocus: false,
        options: {
            labelProperty: "name"
        }
    };

    constructor(props) {
        super(props);
        this.state.isLoading = false;
        this.state.selectedValue = null;
        this.state.resourceSchema = SchemaRegistry.getJsonSchema(props.options.resource);
        this.onBlur = this.onBlur.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        this.fetchAllOptions();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.options.resource !== this.props.options.resource) {
            this.fetchAllOptions();
        }
    }

    componentWillReceiveProps(nextProps) {
        super.componentWillReceiveProps(nextProps);
        if (this.props.options.resource !== nextProps.options.resource)
            this.setState({resourceSchema: SchemaRegistry.getJsonSchema(nextProps.options.resource)});
    }

    getResourceName(props, store) {
        if (props.options) return props.options.resource;
        return null;
    }

    getResourceIdProperty() {
        if (this.props.options && this.props.options.valueProperty) return this.props.options.valueProperty;
        // Use idProperty from the resource schema:
        return this.state.resourceSchema["idProperty"];
    }

    processValue(schema, value) {
        if (value == null || value === "") return undefined;

        switch (schema.type) {
            case "object":
                return this.processObjectValue(schema, value);

            case "array":
                if (!schema.items) return value;
                return value.map(function (v) {
                    return this.processValue(schema.items, v);
                }.bind(this));

            case "boolean":
                if (_.isString(value)) return value.toLowerCase() === "true";
                return !!value;

            case "number":
            case "integer":
                return asNumber(value);

            default:
                return value;
        }
    }

    /**
     *
     * @param {Object} schema JSON Schema
     * @param {String} value JSON Schema idProperty field value
     * @return {Object} Object corresponding to the JSON Schema and filled with values from selected resource
     */
    processObjectValue(schema, value) {
        let idProperty = this.getResourceIdProperty();
        let idPropertyValue = this.processValue(schema.properties[idProperty], value);

        let data = this.state.resource.data;
        if (!data) data = [{[idProperty]: idPropertyValue}];

        let selectedRecord = _.find(data, (row) => String(row[idProperty]) === String(idPropertyValue));
        if (!selectedRecord && this.state.selectedValue) {
            const sv = this.state.selectedValue;
            if (_.isArray(sv)) {
                selectedRecord = _.find(sv, (item) => String(item[idProperty]) === String(idPropertyValue));
            } else if (sv && String(sv[idProperty]) === String(idPropertyValue)) {
                selectedRecord = sv;
            }
        }
        if (!selectedRecord) return {[idProperty]: idPropertyValue};

        let objectValue = {};
        for (let key of _.keys(schema.properties)) {
            if (_.has(selectedRecord, key)) objectValue[key] = selectedRecord[key];
        }
        return objectValue;
    }

    getValue(val, multiple, valueField) {
        if (multiple) {
            return [].slice.call(val).map(o => o[valueField]);
        } else {
            return val[valueField];
        }
    }

    fetchAllOptions() {
        let filter = _.extend({}, this.props.options.filter);
        let labelField = this.props.options.labelProperty;
        if (labelField) {
            filter[labelField + "%"] = "";
        }
        let sort = this.getSort();
        this.actions().getCollection.defer({filter: filter, sort: sort, pageSize: 0});
    }

    getSort() {
        let sortSpec = this.props.options.sort;
        if (sortSpec) return sortSpec;
        let field = this.props.options.labelProperty;
        if (field) return [field];
        field = this.props.options.valueProperty;
        if (field) return [field];
        return null;
    }

    getItemForValue(value, emptyValue, multiple) {
        if (!multiple) return this.getSingleValue(value, emptyValue);
        let result = [];
        for (let v of value) {
            let singleResult = this.getSingleValue(v, undefined);
            if (singleResult == null) continue;
            result.push(singleResult);
        }
        if (result.length == 0) return emptyValue;
        return result;
    }

    getSingleValue(value, emptyValue) {
        if (value == null) return emptyValue;
        if (_.isObject(value)) return value;

        let valueField = this.getResourceIdProperty();

        if (this.state.resource.data != null) {
            let item = _.find(this.state.resource.data, (row) => String(row[valueField]) === String(value));
            if (item) return item;
            if (this.state.selectedValue) {
                if (_.isArray(this.state.selectedValue)) {
                    item = _.find(this.state.selectedValue, (row) => String(row[valueField]) === String(value));
                    if (item) return item;
                } else {
                    if (String(this.state.selectedValue[valueField]) === String(value)) return this.state.selectedValue;
                }
            }
        } else if (this.props.options.enumOptions) {
            let item = _.find(this.props.options.enumOptions, (row) => String(row[valueField]) === String(value));
            if (item) return item;
        }

        // No option found:
        return emptyValue;
    }

    onBlur(event) {
        if (!this.props.onBlur || this.props.multiple) return;
        let valueField = this.getResourceIdProperty();
        this.props.onBlur(
            this.props.id,
            this.processValue(this.props.schema, this.getValue(event.target, this.props.multiple, valueField))
        );
    }

    onChange(newValue, actionMeta) {
        let valueField = this.getResourceIdProperty();
        let value = (newValue == null ? null : this.processValue(this.props.schema, this.getValue(newValue, this.props.multiple, valueField)));
        this.setState({ selectedValue: newValue }, () => {
            this.props.onChange(value);
        });
    }

    render() {
        const {
            id,
            options,
            value,
            disabled,
            readonly,
            multiple,
            autofocus
        } = this.props;
        const { searchable } = options;
        const emptyValue = multiple ? [] : null;

        let labelField = this.props.options.labelProperty || "name";
        let valueField = this.getResourceIdProperty();
        const optionList = (this.state.resource && this.state.resource.data) ? this.state.resource.data : [];
        return (
            <Select
                id={id}
                className={this.props.className}
                isMulti={multiple}
                value={this.getItemForValue(value, emptyValue, multiple)}
                isSearchable={searchable !== false}
                isDisabled={disabled || readonly}
                isClearable={true}
                autoFocus={autofocus}
                openOnFocus={true}
                onBlur={this.onBlur}
                onChange={this.onChange}
                options={optionList}
                getOptionLabel={(o) => (o && o[labelField] != null ? String(o[labelField]) : "")}
                getOptionValue={(o) => (o && o[valueField] != null ? String(o[valueField]) : "")}
                isLoading={this.state.isLoading}
                noOptionsMessage={() => t("No results found")}
                escapeClearsValue={true}
                backspaceRemovesValue={true}
                styles={{menu: (base) => ({
                        ...base,
                        zIndex: "5 !important"
                    })}}
            />
        );
    }
}

export default ObjectSelectWidget;
